import React, { useState } from 'react';
import { Megaphone, Search, Calendar, AlertCircle, ChevronRight } from 'lucide-react';
import { useActiveAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementDetailModal } from '../components/AnnouncementDetailModal';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { Announcement, AnnouncementCategory, AnnouncementPriority } from '@lms/shared/types';

export function Announcements() {
  const { data: announcements, isLoading } = useActiveAnnouncements();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  const { language, t } = useLanguage();
  const items = announcements || [];

  const filteredItems = items.filter(item => {
    const matchesSearch =
      !searchQuery.trim() ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPriorityBadge = (priority: AnnouncementPriority) => {
    switch (priority) {
      case 'URGENT':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-error-container text-on-error-container">
            <AlertCircle size={11} />
            {t(copy.announcements.prioUrgent)}
          </span>
        );
      case 'HIGH':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-warning-container text-on-warning-container">
            {t(copy.announcements.prioHigh)}
          </span>
        );
      default:
        return null;
    }
  };

  const getCategoryLabel = (category: AnnouncementCategory) => {
    switch (category) {
      case 'ACADEMIC':
        return t(copy.announcements.catAcademic);
      case 'IMPORTANT':
        return t(copy.announcements.catImportant);
      case 'MAINTENANCE':
        return t(copy.announcements.catMaintenance);
      case 'EVENT':
        return t(copy.announcements.catEvent);
      default:
        return t(copy.announcements.catGeneral);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 text-primary rounded-2xl">
            <Megaphone size={26} />
          </div>
          <div>
            <h1 className="text-3xl font-headline font-extrabold text-on-surface tracking-tight m-0">
              <Bilingual {...copy.announcements.title} />
            </h1>
            <p className="text-sm font-body text-on-surface-variant m-0 mt-0.5">
              <Bilingual {...copy.announcements.subtitle} />
            </p>
          </div>
        </div>
      </div>

      {/* Filter Surface */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-surface-container-low/50 p-4 rounded-2xl">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant/40 size-4" />
          <input
            type="text"
            className="w-full bg-surface-container-lowest rounded-xl pl-10 pr-4 py-2.5 text-sm font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all border border-on-surface/5"
            placeholder={language === 'ar' ? 'البحث في الإعلانات...' : 'Cari pengumuman...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          {[
            { id: '', label: language === 'ar' ? 'الكل' : 'Semua' },
            { id: 'ACADEMIC', label: t(copy.announcements.catAcademic) },
            { id: 'IMPORTANT', label: t(copy.announcements.catImportant) },
            { id: 'MAINTENANCE', label: t(copy.announcements.catMaintenance) },
            { id: 'EVENT', label: t(copy.announcements.catEvent) },
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-label font-bold transition-all shrink-0 ${
                selectedCategory === cat.id
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="p-12 text-center text-on-surface-variant font-body">
            <Bilingual {...copy.common.loading} />
          </div>
        ) : filteredItems.length > 0 ? (
          filteredItems.map(item => (
            <div
              key={item.id}
              onClick={() => setSelectedAnnouncement(item)}
              className="p-6 bg-surface-container-lowest rounded-2xl shadow-level1 hover:shadow-level2 transition-all cursor-pointer border border-on-surface/5 group"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-label font-bold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
                    {getCategoryLabel(item.category)}
                  </span>
                  {getPriorityBadge(item.priority)}
                </div>
                <span className="text-xs font-body text-on-surface-variant opacity-60 flex items-center gap-1">
                  <Calendar size={13} />
                  {formatDate(item.createdAt)}
                </span>
              </div>

              <h2 className="text-lg font-headline font-bold text-on-surface group-hover:text-primary transition-colors mb-2">
                {item.title}
              </h2>

              <p className="text-sm font-body text-on-surface-variant line-clamp-3 leading-relaxed mb-4">
                {item.content}
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-on-surface/5 text-xs font-label font-bold text-primary">
                <span className="flex items-center gap-1.5 group-hover:underline">
                  <Bilingual {...copy.announcements.readMore} />
                  <ChevronRight size={14} />
                </span>
                {item.program && (
                  <span className="text-on-surface-variant font-mono opacity-60 text-[11px]">
                    [{item.program.code}] {item.program.name}
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="p-16 text-center bg-surface-container-lowest rounded-2xl border border-on-surface/5">
            <Megaphone size={36} className="mx-auto text-on-surface-variant/30 mb-3" />
            <p className="text-sm font-body text-on-surface-variant">
              <Bilingual {...copy.announcements.noAnnouncements} />
            </p>
          </div>
        )}
      </div>

      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />
    </div>
  );
}
